/**
 * App Store Service
 * Handles all API calls for the app store module
 */

import baseAPIService from '../../../../services/baseapiservice';
import type {
  App,
  AppCategory,
  ReviewCategory,
  Review,
  AppRequest,
  Screenshot,
  Changelog,
  Roadmap,
  AppFilters
} from './types';

// Base path - /apiv1/ is already in baseAPIService
const API_BASE = 'core/appstore';

// ======================
// APP CATEGORY SERVICES
// ======================

export const appCategoryService = {
  /**
   * Get all app categories
   */
  getAll: async (): Promise<AppCategory[]> => {
    try {
      const response = await baseAPIService.get(`${API_BASE}/app-category`);
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching app categories:', error);
      throw error;
    }
  },

  /**
   * Get app category by ID
   */
  getById: async (id: number): Promise<AppCategory> => {
    try {
      const response = await baseAPIService.get(`${API_BASE}/app-category/${id}`);
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`Error fetching app category ${id}:`, error);
      throw error;
    }
  },

  /**
   * Create or update app category
   */
  save: async (category: AppCategory): Promise<AppCategory> => {
    try {
      const response = await baseAPIService.post(`${API_BASE}/app-category`, category);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error saving app category:', error);
      throw error;
    }
  },

  /**
   * Delete app category
   */
  delete: async (id: number): Promise<void> => {
    try {
      await baseAPIService.delete(`${API_BASE}/app-category/${id}`);
    } catch (error) {
      console.error(`Error deleting app category ${id}:`, error);
      throw error;
    }
  }
};

// ============================
// REVIEW CATEGORY SERVICES
// ============================

export const reviewCategoryService = {
  /**
   * Get all review categories
   */
  getAll: async (): Promise<ReviewCategory[]> => {
    try {
      const response = await baseAPIService.get(`${API_BASE}/review-category`);
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching review categories:', error);
      throw error;
    }
  },

  /**
   * Get review category by ID
   */
  getById: async (id: number): Promise<ReviewCategory> => {
    try {
      const response = await baseAPIService.get(`${API_BASE}/review-category/${id}`);
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`Error fetching review category ${id}:`, error);
      throw error;
    }
  },

  /**
   * Create or update review category
   */
  save: async (category: ReviewCategory): Promise<ReviewCategory> => {
    try {
      const response = await baseAPIService.post(`${API_BASE}/review-category`, category);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error saving review category:', error);
      throw error;
    }
  },

  /**
   * Delete review category
   */
  delete: async (id: number): Promise<void> => {
    try {
      await baseAPIService.delete(`${API_BASE}/review-category/${id}`);
    } catch (error) {
      console.error(`Error deleting review category ${id}:`, error);
      throw error;
    }
  }
};

// =================
// APP SERVICES
// =================

export const appService = {
  /**
   * Get all apps with optional filters
   */
  getAll: async (filters?: AppFilters): Promise<App[]> => {
    try {
      const response = await baseAPIService.get(`${API_BASE}/app`, { params: filters });
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching apps:', error);
      throw error;
    }
  },

  /**
   * Get app by ID
   */
  getById: async (id: number): Promise<App> => {
    try {
      const response = await baseAPIService.get(`${API_BASE}/app/${id}`);
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`Error fetching app ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get apps by category
   */
  getByCategory: async (categoryId: number): Promise<App[]> => {
    try {
      const response = await baseAPIService.get(`${API_BASE}/app/category/${categoryId}`);
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error(`Error fetching apps for category ${categoryId}:`, error);
      throw error;
    }
  },

  /**
   * Create or update app
   */
  save: async (app: App): Promise<App> => {
    try {
      const response = await baseAPIService.post(`${API_BASE}/app`, app);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error saving app:', error);
      throw error;
    }
  },

  /**
   * Delete app
   */
  delete: async (id: number): Promise<void> => {
    try {
      await baseAPIService.delete(`${API_BASE}/app/${id}`);
    } catch (error) {
      console.error(`Error deleting app ${id}:`, error);
      throw error;
    }
  }
};

// ===================
// REVIEW SERVICES
// ===================

export const reviewService = {
  /**
   * Get all reviews
   */
  getAll: async (): Promise<Review[]> => {
    try {
      const response = await baseAPIService.get(`${API_BASE}/review`);
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching reviews:', error);
      throw error;
    }
  },

  /**
   * Get review by ID
   */
  getById: async (id: number): Promise<Review> => {
    try {
      const response = await baseAPIService.get(`${API_BASE}/review/${id}`);
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`Error fetching review ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get reviews for a specific app
   */
  getByAppId: async (appId: number): Promise<Review[]> => {
    try {
      const response = await baseAPIService.get(`${API_BASE}/review/app/${appId}`);
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error(`Error fetching reviews for app ${appId}:`, error);
      throw error;
    }
  },

  /**
   * Create or update review
   */
  save: async (review: Review): Promise<Review> => {
    try {
      const response = await baseAPIService.post(`${API_BASE}/review`, review);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error saving review:', error);
      throw error;
    }
  },

  /**
   * Delete review
   */
  delete: async (id: number): Promise<void> => {
    try {
      await baseAPIService.delete(`${API_BASE}/review/${id}`);
    } catch (error) {
      console.error(`Error deleting review ${id}:`, error);
      throw error;
    }
  }
};

// ====================
// REQUEST SERVICES
// ====================

export const requestService = {
  /**
   * Get all app requests
   */
  getAll: async (): Promise<AppRequest[]> => {
    try {
      const response = await baseAPIService.get(`${API_BASE}/request`);
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching app requests:', error);
      throw error;
    }
  },

  /**
   * Get request by ID
   */
  getById: async (id: number): Promise<AppRequest> => {
    try {
      const response = await baseAPIService.get(`${API_BASE}/request/${id}`);
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`Error fetching request ${id}:`, error);
      throw error;
    }
  },

  /**
   * Create or update app request
   */
  save: async (request: AppRequest): Promise<AppRequest> => {
    try {
      const response = await baseAPIService.post(`${API_BASE}/request`, request);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error saving app request:', error);
      throw error;
    }
  },

  /**
   * Delete app request
   */
  delete: async (id: number): Promise<void> => {
    try {
      await baseAPIService.delete(`${API_BASE}/request/${id}`);
    } catch (error) {
      console.error(`Error deleting request ${id}:`, error);
      throw error;
    }
  }
};

// ======================
// SCREENSHOT SERVICES
// ======================

export const screenshotService = {
  /**
   * Get screenshot by ID
   */
  getById: async (id: number): Promise<Screenshot> => {
    try {
      const response = await baseAPIService.get(`${API_BASE}/screenshot/${id}`);
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`Error fetching screenshot ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get screenshots for a specific app
   */
  getByAppId: async (appId: number): Promise<Screenshot[]> => {
    try {
      const response = await baseAPIService.get(`${API_BASE}/screenshot/app/${appId}`);
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error(`Error fetching screenshots for app ${appId}:`, error);
      throw error;
    }
  },

  /**
   * Create or update screenshot
   */
  save: async (screenshot: Screenshot): Promise<Screenshot> => {
    try {
      const response = await baseAPIService.post(`${API_BASE}/screenshot`, screenshot);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error saving screenshot:', error);
      throw error;
    }
  },

  /**
   * Delete screenshot
   */
  delete: async (id: number): Promise<void> => {
    try {
      await baseAPIService.delete(`${API_BASE}/screenshot/${id}`);
    } catch (error) {
      console.error(`Error deleting screenshot ${id}:`, error);
      throw error;
    }
  }
};

// ======================
// CHANGELOG SERVICES
// ======================

export const changelogService = {
  /**
   * Get changelog by ID
   */
  getById: async (id: number): Promise<Changelog> => {
    try {
      const response = await baseAPIService.get(`${API_BASE}/changelog/${id}`);
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`Error fetching changelog ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get changelogs for a specific app
   */
  getByAppId: async (appId: number): Promise<Changelog[]> => {
    try {
      const response = await baseAPIService.get(`${API_BASE}/changelog/app/${appId}`);
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error(`Error fetching changelogs for app ${appId}:`, error);
      throw error;
    }
  },

  /**
   * Create or update changelog
   */
  save: async (changelog: Changelog): Promise<Changelog> => {
    try {
      const response = await baseAPIService.post(`${API_BASE}/changelog`, changelog);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error saving changelog:', error);
      throw error;
    }
  },

  /**
   * Delete changelog
   */
  delete: async (id: number): Promise<void> => {
    try {
      await baseAPIService.delete(`${API_BASE}/changelog/${id}`);
    } catch (error) {
      console.error(`Error deleting changelog ${id}:`, error);
      throw error;
    }
  }
};

// ====================
// ROADMAP SERVICES
// ====================

export const roadmapService = {
  /**
   * Get roadmap by ID
   */
  getById: async (id: number): Promise<Roadmap> => {
    try {
      const response = await baseAPIService.get(`${API_BASE}/roadmap/${id}`);
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`Error fetching roadmap ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get roadmap items for a specific app
   */
  getByAppId: async (appId: number): Promise<Roadmap[]> => {
    try {
      const response = await baseAPIService.get(`${API_BASE}/roadmap/app/${appId}`);
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error(`Error fetching roadmap for app ${appId}:`, error);
      throw error;
    }
  },

  /**
   * Create or update roadmap item
   */
  save: async (roadmap: Roadmap): Promise<Roadmap> => {
    try {
      const response = await baseAPIService.post(`${API_BASE}/roadmap`, roadmap);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error saving roadmap:', error);
      throw error;
    }
  },

  /**
   * Delete roadmap item
   */
  delete: async (id: number): Promise<void> => {
    try {
      await baseAPIService.delete(`${API_BASE}/roadmap/${id}`);
    } catch (error) {
      console.error(`Error deleting roadmap ${id}:`, error);
      throw error;
    }
  }
};

// Export all services as default
export default {
  appCategory: appCategoryService,
  reviewCategory: reviewCategoryService,
  app: appService,
  review: reviewService,
  request: requestService,
  screenshot: screenshotService,
  changelog: changelogService,
  roadmap: roadmapService
};
